/**
 * Lots panel for the Photo List Settings page: the canonical lot-name
 * vocabulary photographers see on the sheet (and the names priority scoring
 * matches on), with what still uses each lot — alias rules, stores' NEW
 * walking orders and USED front-lot boosts (the DealerPlatesView
 * rooftopCountByPlateId coupling, ~line 201). The server refuses to delete a
 * lot while any of those use it, and the reason shows on the lot's own row.
 *
 * The Stargard Address Aliases that point at these lots render separately,
 * at the top of the page (StargardAliasesByFile — Ian, 2026-09-23).
 * Edit-in-place autosave; add form; window.confirm deletes.
 */
import React, { useMemo, useState } from "react"
import { Trash2 } from "lucide-react"

import {
  PhotoListLot,
  PhotoListNewLotPriority,
  PhotoListStargardAlias,
  PhotoListUsedLotBoost,
  createPhotoListLot,
  deletePhotoListLot,
  updatePhotoListLot,
} from "../../api/photoList"
import { THEME } from "../../config/theme"
import { useIsMobile } from "../../hooks/useIsMobile"

interface Props {
  lots: PhotoListLot[]
  /** These three only count what still uses each lot. */
  aliases: PhotoListStargardAlias[]
  newLotPriorities: PhotoListNewLotPriority[]
  usedLotBoosts: PhotoListUsedLotBoost[]
  editable: boolean
  dealerGroupId: number
  onLotSaved: (row: PhotoListLot) => void
  onStructureChanged: () => void
  onError: (message: string | null) => void
}

/** What still uses a lot. Any of it blocks deleting the lot (server-side). */
interface LotUsage {
  aliases: number
  walkingOrders: number
  boosts: number
}

const NO_USAGE: LotUsage = { aliases: 0, walkingOrders: 0, boosts: 0 }

export default function StargardMappingPanel({
  lots,
  aliases,
  newLotPriorities,
  usedLotBoosts,
  editable,
  dealerGroupId,
  onLotSaved,
  onStructureChanged,
  onError,
}: Props) {
  // Counting aliases alone called a lot "Unused" while a store's walking
  // order still listed it, and every delete of it was refused (Ian,
  // 2026-09-28: "when i try to delete unused lots they dont delete").
  const usageByLotId = useMemo(() => {
    const usage: Record<number, LotUsage> = {}
    const count = (lotId: number | null, kind: keyof LotUsage) => {
      if (lotId === null) return
      const entry = usage[lotId] ?? { ...NO_USAGE }
      entry[kind] += 1
      usage[lotId] = entry
    }
    for (const alias of aliases) count(alias.lot_id, "aliases")
    for (const row of newLotPriorities) count(row.lot_id, "walkingOrders")
    for (const row of usedLotBoosts) count(row.lot_id, "boosts")
    return usage
  }, [aliases, newLotPriorities, usedLotBoosts])

  return (
    <LotsSection
      lots={lots}
      usageByLotId={usageByLotId}
      editable={editable}
      dealerGroupId={dealerGroupId}
      onLotSaved={onLotSaved}
      onStructureChanged={onStructureChanged}
      onError={onError}
    />
  )
}

// ---------------------------------------------------------------------------
// Lots
// ---------------------------------------------------------------------------

function LotsSection({
  lots,
  usageByLotId,
  editable,
  dealerGroupId,
  onLotSaved,
  onStructureChanged,
  onError,
}: {
  lots: PhotoListLot[]
  usageByLotId: Record<number, LotUsage>
  editable: boolean
  dealerGroupId: number
  onLotSaved: (row: PhotoListLot) => void
  onStructureChanged: () => void
  onError: (message: string | null) => void
}) {
  const [draftName, setDraftName] = useState("")
  const [adding, setAdding] = useState(false)

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault()
    const name = draftName.trim()
    if (!name) return
    setAdding(true)
    onError(null)
    try {
      await createPhotoListLot(name, dealerGroupId)
      setDraftName("")
      onStructureChanged()
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to add lot")
    } finally {
      setAdding(false)
    }
  }

  return (
    <section style={panelStyle}>
      <div style={panelTitleStyle}>Lots</div>
      <p style={panelSubtitleStyle}>
        The lot names photographers see in the Stargard column — and the
        vocabulary walking-order priorities match on. Renaming a lot can
        disconnect built-in rules that match it by name; deleting one is
        blocked while an alias, a walking order or a front-lot boost still
        uses it.
      </p>

      {lots.map((lot) => (
        <LotRow
          key={lot.id ?? `default-${lot.name}`}
          lot={lot}
          usage={lot.id !== null ? usageByLotId[lot.id] ?? NO_USAGE : NO_USAGE}
          editable={editable && lot.id !== null}
          dealerGroupId={dealerGroupId}
          onLotSaved={onLotSaved}
          onStructureChanged={onStructureChanged}
          onError={onError}
        />
      ))}

      {editable && (
        <form onSubmit={handleAdd} style={addFormStyle}>
          <input
            type="text"
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            placeholder="New lot name"
            style={{ ...inputStyle, flex: 1 }}
          />
          <button
            type="submit"
            disabled={!draftName.trim() || adding}
            style={addButtonStyle(!draftName.trim() || adding)}
          >
            {adding ? "Adding…" : "Add Lot"}
          </button>
        </form>
      )}
    </section>
  )
}

/** "Unused", or what uses the lot: "Used by 2 aliases, 1 walking order". */
function usageLabel(usage: LotUsage): string {
  const parts = [
    countOf(usage.aliases, "alias", "aliases"),
    countOf(usage.walkingOrders, "walking order", "walking orders"),
    countOf(usage.boosts, "front-lot boost", "front-lot boosts"),
  ].filter((part): part is string => part !== null)
  return parts.length === 0 ? "Unused" : `Used by ${parts.join(", ")}`
}

function countOf(count: number, one: string, many: string): string | null {
  if (count === 0) return null
  return `${count} ${count === 1 ? one : many}`
}

function LotRow({
  lot,
  usage,
  editable,
  dealerGroupId,
  onLotSaved,
  onStructureChanged,
  onError,
}: {
  lot: PhotoListLot
  usage: LotUsage
  editable: boolean
  dealerGroupId: number
  onLotSaved: (row: PhotoListLot) => void
  onStructureChanged: () => void
  onError: (message: string | null) => void
}) {
  const isMobile = useIsMobile()
  const [nameDraft, setNameDraft] = useState(lot.name)
  // Why the server refused this lot's rename or delete. Shown on the row:
  // the page's banner sits far above this panel, so a refused delete looked
  // like nothing happened.
  const [rowError, setRowError] = useState<string | null>(null)
  const usageText = usageLabel(usage)
  React.useEffect(() => setNameDraft(lot.name), [lot.name])
  // Once what uses the lot changes (it left a walking order, say), the
  // reason no longer holds.
  React.useEffect(() => setRowError(null), [usageText])

  const handleNameBlur = async () => {
    const name = nameDraft.trim()
    if (lot.id === null || !name || name === lot.name) {
      setNameDraft(lot.name)
      return
    }
    onError(null)
    setRowError(null)
    try {
      const saved = await updatePhotoListLot(lot.id, { name }, dealerGroupId)
      onLotSaved(saved)
      if (saved.code_references_may_break) {
        onError(
          `Renamed "${lot.name}" to "${saved.name}". Built-in walking-order ` +
            `rules that matched the old name no longer apply — check NEW ` +
            `Walking Order and USED Front-Lot Boosts.`,
        )
      }
    } catch (err) {
      setRowError(err instanceof Error ? err.message : "Failed to rename lot")
      setNameDraft(lot.name)
    }
  }

  const handleDelete = async () => {
    if (lot.id === null) return
    if (!window.confirm(`Delete lot "${lot.name}"?`)) return
    onError(null)
    setRowError(null)
    try {
      await deletePhotoListLot(lot.id, dealerGroupId)
      onStructureChanged()
    } catch (err) {
      setRowError(err instanceof Error ? err.message : "Failed to delete lot")
    }
  }

  return (
    <div style={lotRowStyle(isMobile)}>
      <input
        type="text"
        value={nameDraft}
        disabled={!editable}
        onChange={(e) => setNameDraft(e.target.value)}
        onBlur={handleNameBlur}
        onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
        style={{ ...inputStyle, width: "100%", minWidth: 0 }}
      />
      <span style={usageTagStyle}>{usageText}</span>
      {editable ? (
        <button
          type="button"
          onClick={handleDelete}
          aria-label={`Delete ${lot.name}`}
          style={trashButtonStyle}
        >
          <Trash2 size={15} strokeWidth={2} />
        </button>
      ) : (
        <span />
      )}
      {rowError && (
        <div role="alert" style={rowErrorStyle}>
          {rowError}
        </div>
      )}
    </div>
  )
}

// --- Local styles (panel chrome from DealerPlatesView.tsx ~lines 500-519) ---

const panelStyle: React.CSSProperties = {
  background: THEME.colors.neutral.light.surface,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  padding: THEME.spacing.md,
  marginBottom: THEME.spacing.lg,
}

const panelTitleStyle: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 500,
  color: THEME.colors.neutral.light.textPrimary,
  marginBottom: 4,
}

const panelSubtitleStyle: React.CSSProperties = {
  fontSize: 13,
  color: THEME.colors.neutral.light.textSecondary,
  marginTop: 0,
  marginBottom: THEME.spacing.md,
  lineHeight: 1.4,
}

// The usage tag can run to "Used by 2 aliases, 1 walking order, 1 front-lot
// boost", so it gets a fixed column it wraps inside; on mobile a narrower
// one, because a 390px viewport can't fit minmax(160px,...) + 200px + 40px.
const lotRowStyle = (isMobile: boolean): React.CSSProperties => ({
  display: "grid",
  gridTemplateColumns: isMobile ? "minmax(0, 1fr) 110px 40px" : "minmax(160px, 1fr) 200px 40px",
  gap: THEME.spacing.sm,
  alignItems: "center",
  padding: `${THEME.spacing.xs} 0`,
  borderTop: `1px solid ${THEME.colors.silver}`,
})

const inputStyle: React.CSSProperties = {
  boxSizing: "border-box",
  padding: `6px ${THEME.spacing.sm}`,
  fontSize: 13,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.sm,
  fontFamily: THEME.fonts.primary,
  background: THEME.colors.white,
}

const usageTagStyle: React.CSSProperties = {
  fontSize: 11,
  color: THEME.colors.neutral.light.textSecondary,
  fontStyle: "italic",
}

// Spans the whole row, under the name, tag and delete button.
const rowErrorStyle: React.CSSProperties = {
  gridColumn: "1 / -1",
  fontSize: 12,
  lineHeight: 1.4,
  color: THEME.colors.semantic.danger,
}

const trashButtonStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "2px 6px",
  background: "transparent",
  border: "none",
  cursor: "pointer",
  color: THEME.colors.semantic.danger,
}

const addFormStyle: React.CSSProperties = {
  display: "flex",
  gap: THEME.spacing.sm,
  marginTop: THEME.spacing.md,
}

const addButtonStyle = (disabled: boolean): React.CSSProperties => ({
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  fontSize: 13,
  fontWeight: 500,
  background: THEME.colors.navy,
  color: THEME.colors.white,
  border: "none",
  borderRadius: THEME.borderRadius.md,
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.5 : 1,
  fontFamily: THEME.fonts.primary,
  whiteSpace: "nowrap",
})
